import { CASE_FETCH_FOLDERS_SUCCESS } from 'features/case/redux/constants';
import { selectFolders } from './selectors';
import history from './history';

const CONTAINER_SEGMENT_TO_STATE_KEY = {
  bundles: 'bundles',
  teamBundles: 'teamBundles',
  privateBundles: 'privateBundles',
  trialbundles: 'trialbooks',
  'court-bundles': 'courtBundles',
  'group-bundles': 'groupBundles',
  'quick-links': 'quickLinks',
};

const STOP_SEGMENTS = new Set(['files', 'annotationId', 'compositeKey']);
const MAX_FOLDER_DEPTH = 6; // Limit folder nesting depth beneath the container

const normalizePath = (path) => (path || '').replace(/\/+$/, '');

const extractContainerInfo = (pathname) => {
  const segments = (pathname || '').split('/').filter(Boolean);
  if (segments[0] !== 'case') return null;

  const containerIndex = segments.findIndex((seg) => CONTAINER_SEGMENT_TO_STATE_KEY[seg]);
  if (containerIndex === -1) return null;

  const containerSegment = segments[containerIndex];
  const containerCode = segments[containerIndex + 1];
  if (!containerCode) return null;

  const folderSegments = [];
  let containsStopSegment = false;
  for (let i = containerIndex + 2; i < segments.length; i += 1) {
    const segment = segments[i];
    if (STOP_SEGMENTS.has(segment)) {
      containsStopSegment = true;
      break;
    }
    folderSegments.push(segment);
  }

  const prefixSegments = segments.slice(0, containerIndex + 2);

  return { containerSegment, containerCode, folderSegments, prefixSegments, containsStopSegment };
};

const walkFolderPath = (rootFolders, codes) => {
  if (!Array.isArray(rootFolders) || codes.length === 0) return { folder: null, matchedCount: 0 };

  let currentFolder = rootFolders.find((folder) => folder?.code === codes[0]);
  if (!currentFolder) return { folder: null, matchedCount: 0 };

  let matchedCount = 1; // container code matched

  for (let i = 1; i < codes.length; i += 1) {
    if (matchedCount - 1 >= MAX_FOLDER_DEPTH) {
      // Reached max allowed depth
      return { folder: currentFolder, matchedCount };
    }

    const hasChildren = Array.isArray(currentFolder.folders) && currentFolder.folders.length > 0;
    if (!hasChildren) {
      // Reached a leaf before consuming all segments; stop here.
      return { folder: currentFolder, matchedCount };
    }

    const nextFolder = currentFolder.folders.find((folder) => folder?.code === codes[i]);
    if (!nextFolder) {
      // Segment not found under this parent; stop at current valid folder.
      return { folder: currentFolder, matchedCount };
    }

    currentFolder = nextFolder;
    matchedCount += 1;
  }

  return { folder: currentFolder, matchedCount };
};

const buildFirstLeafPath = (folder, visited = new Set(), depth = 0, maxDepth = MAX_FOLDER_DEPTH) => {
  if (!folder || !Array.isArray(folder.folders) || folder.folders.length === 0) return [];
  if (depth >= maxDepth) return [];
  if (!folder.code) return [];
  if (visited.has(folder.code)) return [];

  const nextVisited = new Set(visited);
  nextVisited.add(folder.code);

  for (let i = 0; i < folder.folders.length; i += 1) {
    const child = folder.folders[i];
    if (!child || !child.code) continue;
    const childPath = buildFirstLeafPath(child, nextVisited, depth + 1, maxDepth);
    return [child.code, ...childPath];
  }

  return [];
};

// Redirect folder routes that land on parent nodes to the first leaf descendant
let lastHandledPath = '';

const folderNavigationMiddleware =
  ({ getState }) =>
  (next) =>
  (action) => {
    const result = next(action);

    const state = getState();
    const pathname =
      action?.payload?.location?.pathname ||
      (history && history.location && history.location.pathname) ||
      state?.matcher?.url;
    if (!pathname) return result;

    const normalizedCurrentPath = normalizePath(pathname);
    // Skip redundant processing for the same path unless folders were refreshed.
    if (normalizedCurrentPath === lastHandledPath && action.type !== CASE_FETCH_FOLDERS_SUCCESS) {
      return result;
    }

    const containerInfo = extractContainerInfo(pathname);
    if (!containerInfo) return result;

    const { containerSegment, containerCode, folderSegments, prefixSegments, containsStopSegment } =
      containerInfo;

    // Do not interrupt routes that already target files/annotations
    if (containsStopSegment) return result;

    const allFolders = selectFolders(state) || {};
    const rootFolders = allFolders[CONTAINER_SEGMENT_TO_STATE_KEY[containerSegment]];
    if (!Array.isArray(rootFolders) || rootFolders.length === 0) return result;

    const limitedFolderSegments = folderSegments.slice(0, MAX_FOLDER_DEPTH);
    const pathCodes = [containerCode, ...limitedFolderSegments];
    const { folder: currentFolder, matchedCount } = walkFolderPath(rootFolders, pathCodes);
    if (!currentFolder) return result;

    const hasChildFolders =
      Array.isArray(currentFolder.folders) && currentFolder.folders.length > 0;
    const remainingDepth = Math.max(MAX_FOLDER_DEPTH - (matchedCount - 1), 0);
    const leafPath =
      hasChildFolders && remainingDepth > 0
        ? buildFirstLeafPath(currentFolder, new Set(), 0, remainingDepth)
        : [];

    // If we consumed all path segments and are already on a leaf (no children), do nothing.
    if (!hasChildFolders && matchedCount === pathCodes.length) return result;

    const matchedFolderSegments = pathCodes.slice(1, matchedCount); // drop container code
    const targetSegments = [...prefixSegments, ...matchedFolderSegments, ...leafPath];
    const targetPath = `/${targetSegments.join('/')}`;
    const normalizedTargetPath = normalizePath(targetPath);

    if (normalizedTargetPath === normalizedCurrentPath) {
      lastHandledPath = normalizedCurrentPath;
      return result;
    }

    const locationSuffix =
      history && history.location
        ? `${history.location.search || ''}${history.location.hash || ''}`
        : '';
    history.replace(`${targetPath}${locationSuffix}`);
    lastHandledPath = normalizedTargetPath;

    return result;
  };

export default folderNavigationMiddleware;
